import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useToggle } from '@/hooks/useToggle';
import Menu, {
  BaseMenuItem,
  MenuDivider,
  type MenuProps,
} from '@/components/Menu';
import {
  AddFileIcon,
  AddFolderIcon,
  CameraIcon,
  FolderIcon,
  SequenceIcon,
  SmileIcon,
} from '@/icons';
import FileInput from '@/components/inputs/FileInput';
import PhotoCaptureDialog from '@/dialogs/PhotoCaptureDialog';
import { classes } from './utils';

export type NewFileMenuProps = {
  open: boolean;
  disableAddFiles?: boolean;
  anchorTo: HTMLElement | null;
  anchorOrigin?: MenuProps['anchorOrigin'];
  transformOrigin?: MenuProps['transformOrigin'];
  onStartSmileSimulation: (file: File) => void;
  onImportSequence: (files: File[]) => void;
  onAddFiles: (files: File[]) => void;
  onAddFileCollection: () => void;
  onOpenUpload: () => void;
  onClose: () => void;
};

export default function NewFileMenu({
  open,
  disableAddFiles,
  anchorTo,
  anchorOrigin,
  transformOrigin,
  onStartSmileSimulation,
  onImportSequence,
  onAddFiles,
  onAddFileCollection,
  onOpenUpload,
  onClose,
}: NewFileMenuProps) {
  const [isPhotoDialogOpen, togglePhotoDialog] = useToggle();

  const handleSelectSmileFile = (files: File[]) => {
    if (files[0]) return onStartSmileSimulation(files[0]);
  };

  return (
    <>
      {isPhotoDialogOpen && (
        <PhotoCaptureDialog
          onStartSimulation={(file) => {
            togglePhotoDialog(false);
            handleSelectSmileFile([file]);
          }}
          onSave={(file) => {
            togglePhotoDialog(false);
            onAddFiles([file]);
          }}
          onClose={() => togglePhotoDialog(false)}
        />
      )}
      <Menu
        anchorTo={anchorTo}
        open={open}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        onClose={onClose}>
        <FileInput
          disabled={disableAddFiles}
          className={classes.input}
          id="smile-simulation"
          accept="image/*"
          onSelect={handleSelectSmileFile}
        />
        <FileInput
          disabled={disableAddFiles}
          className={classes.input}
          id="import-sequence"
          accept=".zip"
          onSelect={onImportSequence}
        />
        <FileInput
          multiple
          disabled={disableAddFiles}
          className={classes.input}
          id="new-files"
          onSelect={onAddFiles}
        />
        {!disableAddFiles && (
          <>
            <BaseMenuItem className={classes.menuItem}>
              <label className={classes.inputLabel} htmlFor="import-sequence">
                <ListItemIcon>
                  <SequenceIcon />
                </ListItemIcon>
                <ListItemText>Import Sequence</ListItemText>
              </label>
            </BaseMenuItem>
            <BaseMenuItem className={classes.menuItem}>
              <label className={classes.inputLabel} htmlFor="smile-simulation">
                <ListItemIcon>
                  <SmileIcon />
                </ListItemIcon>
                <ListItemText>Smile Simulation</ListItemText>
              </label>
            </BaseMenuItem>
            <BaseMenuItem
              className={classes.menuItem}
              onClick={() => togglePhotoDialog(true)}>
              <ListItemIcon>
                <CameraIcon />
              </ListItemIcon>
              <ListItemText>Take a Picture</ListItemText>
            </BaseMenuItem>
            <MenuDivider className={classes.divider} />
            <BaseMenuItem className={classes.menuItem}>
              <label className={classes.inputLabel} htmlFor="new-files">
                <ListItemIcon>
                  <AddFileIcon />
                </ListItemIcon>
                <ListItemText>Upload File(s)</ListItemText>
              </label>
            </BaseMenuItem>
            <MenuDivider className={classes.divider} />
          </>
        )}
        <BaseMenuItem className={classes.menuItem} onClick={onOpenUpload}>
          <ListItemIcon>
            <AddFolderIcon />
          </ListItemIcon>
          <ListItemText>Upload File Collection(s)</ListItemText>
        </BaseMenuItem>
        <BaseMenuItem
          className={classes.menuItem}
          onClick={onAddFileCollection}>
          <ListItemIcon>
            <FolderIcon />
          </ListItemIcon>
          <ListItemText>New File Collection</ListItemText>
        </BaseMenuItem>
      </Menu>
    </>
  );
}
